import { Auth } from "@aws-amplify/auth";
import useAPI from "@toothfairy/shared-api/useApi";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import AppChoice from "@toothfairy/shared-ui/AppChoice";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppModal from "@toothfairy/shared-ui/AppModal";
import AppSecureTextInput from "@toothfairy/shared-ui/AppSecureTextInput";
import AppSeparator from "@toothfairy/shared-ui/AppSeparator";
import AppSwitch from "@toothfairy/shared-ui/AppSwitch";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import { AppText } from "@toothfairy/shared-ui";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, StyleSheet, View, useWindowDimensions } from "react-native";
import envConfig from "../../../../envConfig";
import Files from "../../../API/Files";
import trainingAPI from "../../../API/Training/trainingAPI";
import ImagePlaceholder from "../../../Components/ImagePlaceholder";
import LanguageSelector from "../../../Components/LanguageSelector";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { getWebAppUrl } from "../../../Utils/regionWebUrl";
import AdminInfoLeaf from "../../Admin/Components/SubComponents/AdminInfoLeaf";
import { isLargeScreen } from "../../MainViewContainer";
import { convertImageToPng } from "../../../Utility/imageUtils";
import { userType as getUserType } from "../../../../App";
import { UserType } from "../../../models";
import { useHistory } from "../../../Router";
import { routes } from "../../../Router/routes";
import useOnboardingTour from "../../../Hooks/useOnboardingTour";

const ProfileSettingsScreen = ({
  handleFormSubmit,
  handleUserUpdate,
  formValidationSchema,
  onboardingData,
  user,
  workspace,
  handleUserDeletion,
  handleNewsletterToggle,
}) => {
  const { width } = useWindowDimensions();
  const { currentTheme, setMode, mode } = AppTheme.useTheme();
  const styles = styleGenerator(currentTheme);
  const AppToasty = AppToast.useToast();
  const { state } = AppStateManager.useAppStateManager();
  const { getActiveWorkspace, isWorkspaceDisabled } = useWorkspaces();
  const workspaceid = getActiveWorkspace()?.id;
  const isElectron = !!window?.electronAPI;
  const { t } = useTranslation();
  const history = useHistory();
  const { isPlatformSupported, resetOnboarding } = useOnboardingTour();

  const handleReplayOnboarding = () => {
    resetOnboarding();
    AppToasty.show(t("profile.onboardingReplayed"), { type: "success" });
    history.push(routes.workspaces);
  };

  const getOwnedActiveWorkspaces = () => {
    const currentUserType = getUserType();
    return (state?.workspaces || []).filter(
      (ws) => ws?.userID === user?.id && !isWorkspaceDisabled(ws)
    );
  };

  const handleDeleteAccountPress = () => {
    const ownedWorkspaces = getOwnedActiveWorkspaces();
    if (ownedWorkspaces.length > 0) {
      AppToasty.show(
        t("profile.cannotDeleteAccount", {
          workspaces: ownedWorkspaces.map((ws) => ws.title).join(", "),
        }),
        { placement: "top", type: "danger" }
      );
    } else {
      setShowDeletionWarning(true);
    }
  };
  const checkMFAStatus = async () => {
    try {
      if (isElectron) {
        // For Electron users, MFA is managed on the web
        // We don't check MFA status locally
        setMfaEnabled(user?.isMFAEnabled); // Always show "Enable" button for Electron
      } else {
        // For web, use Amplify Auth
        const user = await Auth.currentAuthenticatedUser();
        const preferredMFA = await Auth.getPreferredMFA(user);
        setMfaEnabled(preferredMFA === "SOFTWARE_TOKEN_MFA");
      }
    } catch (error) {
      console.log("Error checking MFA status", error);
      setMfaEnabled(false);
    }
  };

  useEffect(() => {
    checkMFAStatus();
    if (user?.image) {
      getProfileImageUrl(user.image);
    }
  }, [user?.image, user?.isMFAEnabled]);

  const getProfileImageUrl = async (imagePath) => {
    try {
      const _url = await S3downloadUrlRequest({
        filename: `imported-image/root/${imagePath}`,
        context: "pdf",
        workspaceid: "root",
      });
      if (_url?.url) {
        setProfileImage(_url.url);
      }
    } catch (error) {
      console.log("Error getting profile image URL", error);
    }
  };

  const handleProfileImageSelection = async (file) => {
    if (!file) {
      setProfileImage(null);
      handleUserUpdate("image", null);
      return;
    }

    try {
      // Convert to PNG if it's an image but not already PNG
      let processedFile = file;
      if (file.type.startsWith("image/") && file.type !== "image/png") {
        try {
          processedFile = await convertImageToPng(file);
        } catch (error) {
          console.error("Failed to convert image:", error);
          AppToasty.show(t("profile.imageConvertFailed"), {
            placement: "top",
            type: "danger",
          });
          return;
        }
      }

      const filename = processedFile.name;
      const _fileN = String(Date.now()) + filename;
      const contentType = filename.slice(
        (Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1
      );

      const result = await S3uploadUrlRequest({
        filename: `root/${_fileN}`,
        importType: "imported-image",
        contentType,
      });

      const upload = await axios.request({
        method: "put",
        url: JSON.parse(result?.body)?.uploadURL,
        data: processedFile,
        headers: {
          "Content-Type": processedFile.type,
        },
        onUploadProgress: (p) => {
          setFileUploadInProgress(true);
          setFileUploadProgress(Math.round((p.loaded * 100) / p.total));
          if (p.loaded === p.total) {
            setFileUploadInProgress(false);
            setFileUploadProgress(100);
          }
        },
      });

      if (JSON.parse(result?.body).uploadURL) {
        handleUserUpdate("image", _fileN);
        AppToasty.show(t("profile.imageUploadSuccess"), {
          placement: "top",
          type: "success",
        });
      }
    } catch (error) {
      console.log("Error uploading profile image", error);
      AppToasty.show(t("profile.imageUploadFailed"), {
        placement: "top",
        type: "danger",
      });
      setFileUploadInProgress(false);
    }
  };

  const handleMFASetupComplete = (success) => {
    if (success) {
      setMfaEnabled(true);
      setShowMFASetup(false);
      handleUserUpdate("isMFAEnabled", true);
      AppToasty.show(t("profile.mfaEnabled"), {
        placement: "top",
        type: "success",
      });
    }
  };

  const handleDisableMFA = async () => {
    try {
      setIsAsyncProcessInProgress(true);
      const user = await Auth.currentAuthenticatedUser();
      await Auth.setPreferredMFA(user, "NOMFA");
      setMfaEnabled(false);
      handleUserUpdate("isMFAEnabled", false);
      AppToasty.show(t("profile.mfaDisabled"), {
        placement: "top",
        type: "success",
      });
    } catch (error) {
      console.log("Error disabling MFA", error);
      AppToasty.show(t("profile.mfaDisableFailed"), {
        placement: "top",
        type: "danger",
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  const [isEditable, setIsEditable] = useState(false);
  const [showDeletionWarning, setShowDeletionWarning] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [isAsyncProcessInProgress, setIsAsyncProcessInProgress] =
    useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);

  const {
    data: S3uploadUrlData,
    loading: urlUploadGenerationInProgress,
    apiRequest: S3uploadUrlRequest,
    response: S3uploadResponse,
  } = useAPI(trainingAPI.bulkImportUrlGeneration, envConfig);

  const {
    data: S3downloadUrlData,
    loading: urldownloadGenerationInProgress,
    apiRequest: S3downloadUrlRequest,
    response: S3downloadResponse,
  } = useAPI(Files.downloadUrlGeneration, envConfig);

  const userDeletionModal = useMemo(() => {
    return (
      <AppModal
        transparent
        modalStyle={
          Platform.OS === "web"
            ? {
                maxWidth: 600,
                maxHeight: "80%",
                margin: "auto",
                borderRadius: 10,
              }
            : {}
        }
        isModalVisible={showDeletionWarning}
        animationType="none"
        modalBackdrop={currentTheme?.theme?.softBackgroundColor}
        isCloseButtonVisible
        modalTitle=" "
        modalInnerContentStyle={{
          paddingHorizontal: 5,
          paddingVertical: 20,
        }}
        closeModal={() => setShowDeletionWarning(false)}
      >
        <AppChoice
          wrapperStyle={{ alignSelf: "center", maxWidth: 600 }}
          caption={t("profile.deleteAccountCaption")}
          subCaption={t("profile.deleteAccountSubCaption")}
          cancelCaption={t("profile.deleteCancelCaption")}
          confirmCaption={t("profile.deleteConfirmCaption")}
          onConfirmButtonPress={() => {
            handleUserDeletion();
            setShowDeletionWarning(false);
          }}
          onCancelButtonPress={() => {
            setShowDeletionWarning(false);
          }}
          AppChoiceButton={ToothFairyButton}
        />
      </AppModal>
    );
  });

  return (
    <View style={styles.container}>
      {user?.id && (
        <>
          <View style={styles.profileImageSection}>
            <ImagePlaceholder
              onFileSelection={handleProfileImageSelection}
              imagePath={profileImage}
              isFileUploadInProgress={fileUploadInProgress}
              fileUploadProgress={fileUploadProgress}
              placeholderText={t("profile.uploadPhoto")}
              isLargeLogo
              placeholderStyle={{
                marginTop: 30,
              }}
              style={{
                borderRadius: 64,
                height: 128,
              }}
              wrapperStyleCloseIcon={{
                top: 2,
                right: 0,
              }}
              forcedImgHeight={128}
              forcedImgWidth={128}
              forcedImgRadius={64}
            />
          </View>

          <AppForms.AppForm
            style={{
              minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 300,
              maxWidth: 600,
              marginTop: 20,
            }}
            initialValues={{
              name: user?.username,
              timezone: user?.timezone,
              theme: user?.theme,
              workspaceName: workspace?.title,
              workspaceType: workspace?.type,
            }}
            onSubmit={handleFormSubmit}
            validationSchema={formValidationSchema}
          >
            <AdminInfoLeaf
              simpleTextStyle={{
                marginRight: 65,
                // marginLeft: -20,
                marginTop: 10,
                maxWidth: Math.min(width - 55, 600),
                color: currentTheme?.theme?.primary,
              }}
              wrapperStyle={{
                marginBottom: 10,
              }}
              titleStyle={{
                marginTop: 0,
              }}
              title={t("profile.accountId")}
              value={user?.id}
              width={420}
              showSimpleText
              iconSize={16}
            />
            <AppSecureTextInput
              wrapperStyleSecureTextInput={{
                paddingBottom: 10,
                minHeight: 72,
                marginRight: 13,
              }}
              textInputLabelStyle={{
                marginLeft: 0,
              }}
              textInputLabelStyleCore={{
                marginLeft: 0,
              }}
              wrapperInputStyle={{ flex: 1 }}
              actionsButtonsViewStyle={{ paddingRight: 10 }}
              showTextInputLabel
              label={t("common.name")}
              isMandatory
              isEditable={isEditable}
              onEditIconPress={() => setIsEditable(true)}
              onCancelIconPress={() => setIsEditable(false)}
              onSubmitIconPress={(v) => {
                setIsEditable(false);
                handleUserUpdate("username", v);
              }}
              initialValue={user?.username}
            />

            <AppSecureTextInput
              wrapperStyleSecureTextInput={{
                paddingBottom: 10,
                minHeight: 72,
              }}
              textInputLabelStyle={{
                marginLeft: 0,
              }}
              textInputLabelStyleCore={{
                marginLeft: 0,
              }}
              wrapperInputStyle={{ flex: 1 }}
              actionsButtonsViewStyle={{ paddingRight: 10 }}
              showTextInputLabel
              label={t("profile.email")}
              isEditEnabled={false}
              initialValue={user?.user_email}
            />
            <AppDropDown
              showTextInputLabel
              label={t("profile.theme")}
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
                maxWidth: 250,
              }}
              wrapperContainer={{
                paddingBottom: 15,
              }}
              inputLabelStyle={{
                marginLeft: 0,
              }}
              isElevated={false}
              dropdownIconColor="transparent"
              items={onboardingData?.theme?.fieldOptions?.map((opt) => ({
                ...opt,
                internalTitle:
                  opt.id === "light"
                    ? t("profile.themeLightVC")
                    : opt.id === "dark"
                    ? t("profile.themeDarkFounder")
                    : opt.internalTitle,
              }))}
              propertyLabel="internalTitle"
              propertyValue="id"
              selectedValue={mode}
              onValueChange={(itemValue, itemIndex) => {
                setMode(itemValue);
                handleUserUpdate("theme", itemValue);
              }}
            />
            <LanguageSelector
              wrapperStyle={{
                paddingBottom: 15,
              }}
            />
            <AppDropDown
              showTextInputLabel
              label={t("profile.timezone")}
              isMandatory
              dropDownStyle={{
                borderColor: currentTheme?.theme?.border_color,
                maxWidth: 250,
              }}
              wrapperContainer={{
                paddingBottom: 15,
              }}
              inputLabelStyle={{
                marginLeft: 0,
              }}
              isElevated={false}
              dropdownIconColor="transparent"
              items={AppUtilities.timezoneData()}
              propertyLabel="name"
              propertyValue="id"
              selectedValue={user?.timezone}
              onValueChange={(itemValue, itemIndex) => {
                handleUserUpdate("timezone", itemValue);
              }}
            />
          </AppForms.AppForm>
          {isElectron && (
            <AppSeparator
              text={""}
              separatorContainer={{
                marginTop: 10,
              }}
            />
          )}

          <View style={styles.newsletterSection}>
            <AppSwitch
              isTicked={user?.subscribedToNewsletter === true}
              onChange={(value) => handleNewsletterToggle?.(value)}
              showTextInputLabel
              label={t("newsletter.toggleLabel")}
              description={t("newsletter.toggleLabelDesc")}
              descriptionIcon={false}
              descriptionColor={currentTheme?.theme?.mediumGrey}
              pressableStyle={{
                marginRight: 20,
              }}
              descriptionStyle={{
                marginRight: 10,
              }}
              wrapperStyle={{
                paddingVertical: 8,
                paddingHorizontal: 20,
                maxWidth: 600,
                width: "100%",
                alignSelf: "center",
              }}
            />
          </View>

          {isPlatformSupported && (
            <View style={styles.replayOnboardingSection}>
              <ToothFairyButton
                title={t("profile.replayOnboarding")}
                onPress={handleReplayOnboarding}
                fontColor={currentTheme?.theme?.primary}
                backgroundColor={currentTheme?.theme?.transparent}
                wrapperStyle={{
                  marginBottom: 4,
                  minWidth: 200,
                  height: Constants.appButtonHeight,
                }}
              />
              <AppText
                style={{
                  fontSize: currentTheme?.theme?.small_font_size,
                  textAlign: "center",
                  marginTop: 4,
                  paddingHorizontal: 20,
                }}
                color={currentTheme?.theme?.mediumGrey}
              >
                {t("profile.replayOnboardingHint")}
              </AppText>
            </View>
          )}

          <View style={styles.securitySection}>
            {!mfaEnabled ? (
              <>
                <ToothFairyButton
                  title={t("profile.enableMFA")}
                  onPress={() => {
                    if (isElectron) {
                      // Open web browser for MFA setup
                      window.electronAPI.openUrlOnWeb(
                        getWebAppUrl("/profile/profile_settings")
                      );
                    } else {
                      setShowMFASetup(true);
                    }
                  }}
                  fontColor={currentTheme?.theme?.primary}
                  backgroundColor={currentTheme?.theme?.transparent}
                  wrapperStyle={{
                    marginBottom: 20,
                    minWidth: 150,
                    height: Constants.appButtonHeight,
                  }}
                />
                {isElectron && (
                  <AppText
                    style={{
                      fontSize: currentTheme?.theme?.small_font_size,
                      textAlign: "center",
                      marginTop: -10,
                      marginBottom: 10,
                      fontStyle: "italic",
                    }}
                    color={currentTheme?.theme?.mediumGrey}
                    preventTranslation
                  >
                    {t("profile.mfaBrowserSetup")}
                  </AppText>
                )}
              </>
            ) : (
              <>
                <ToothFairyButton
                  title={t("profile.disableMFA")}
                  onPress={() => {
                    if (isElectron) {
                      // Open web browser for MFA management
                      window.electronAPI.openUrlOnWeb(
                        getWebAppUrl("/profile/profile_settings")
                      );
                    } else {
                      handleDisableMFA();
                    }
                  }}
                  isDeletion
                  fontColor={currentTheme?.theme?.orange}
                  isDisabled={isAsyncProcessInProgress}
                  forcedDisabledBackgroundColor={
                    currentTheme?.theme?.transparent
                  }
                  wrapperStyle={{
                    marginBottom: 0,
                    minWidth: 150,
                    height: Constants.appButtonHeight,
                    backgroundColor: currentTheme?.theme?.transparent,
                  }}
                />
                {isElectron && (
                  <AppText
                    style={{
                      fontSize: currentTheme?.theme?.small_font_size,
                      textAlign: "center",
                      marginTop: 10,
                      fontStyle: "italic",
                    }}
                    color={currentTheme?.theme?.mediumGrey}
                    preventTranslation
                  >
                    {t("profile.mfaBrowserManage")}
                  </AppText>
                )}
              </>
            )}
          </View>
          <AppSeparator
            text={t("profile.or")}
            separatorContainer={{
              marginTop: 0,
            }}
          />

          <ToothFairyButton
            title={t("profile.permanentlyDeleteAccount")}
            onPress={handleDeleteAccountPress}
            isDeletion
            wrapperStyle={{
              marginTop: 10,
              minWidth: 150,
              height: Constants.appButtonHeight,
            }}
          />
          {userDeletionModal}

          <AppModal
            transparent
            modalStyle={
              Platform.OS === "web"
                ? {
                    maxWidth: "80%",
                    maxHeight: "90%",
                    margin: "auto",
                    borderRadius: 10,
                  }
                : {}
            }
            isModalVisible={showMFASetup}
            animationType="none"
            modalBackdrop={currentTheme?.theme?.softBackgroundColor}
            isCloseButtonVisible
            modalTitle={t("profile.mfaSetupTitle")}
            modalInnerContentStyle={{
              paddingHorizontal: 20,
              paddingVertical: 20,
              alignItems: "center",
            }}
            closeModal={() => setShowMFASetup(false)}
          >
            <AppAuth.MFASetup
              onMFASetupComplete={handleMFASetupComplete}
              onCancel={() => setShowMFASetup(false)}
              isAsyncProcessInProgress={isAsyncProcessInProgress}
              setIsAsyncProcessInProgress={setIsAsyncProcessInProgress}
            />
          </AppModal>
        </>
      )}
    </View>
  );
};

const styleGenerator = (currentTheme) => {
  return StyleSheet.create({
    container: {
      alignItems: "center",
      flex: 1,
      paddingTop: 0,
    },
    label: {
      fontWeight: currentTheme?.theme?.font_bold,
      marginTop: 20,
    },
    submitBotton: {
      marginTop: 20,
    },
    title: {
      fontSize: currentTheme?.theme?.large_font_size,
      fontWeight: currentTheme?.theme?.font_bold,
      marginBottom: 30,
    },
    securitySection: {
      marginTop: 10,
      alignItems: "center",
      width: "100%",
    },
    newsletterSection: {
      marginBottom: 4,
      alignItems: "center",
      width: 390,
      marginTop: -5,
    },
    replayOnboardingSection: {
      alignItems: "center",
      width: 390,
      marginTop: 8,
      marginBottom: 4,
    },
    profileImageSection: {
      marginTop: 20,
      marginBottom: 10,
      alignItems: "center",
    },
  });
};

export default ProfileSettingsScreen;
